import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { ArrowRight } from "lucide-react";
import { supporters, supporterPath } from "@/data/supporters";
import { tx } from "@/i18n/localized";
import { type Locale } from "@/i18n/config";
import { pageHead } from "@/i18n/head";
import { modenmisupporterStrings } from "@/i18n/content/modenmisupporter";
import { cn } from "@/lib/utils";
import { Img, SIZES } from "@/components/Img";

/** <head> for the "Meet a MI Supporter" listing page. */
export function modenmisupporterHead(locale: Locale) {
  const s = modenmisupporterStrings[locale];
  return pageHead("modenmisupporter", locale, s.meta, { breadcrumbName: s.hero.title });
}

export function SupporterListPage({ locale }: { locale: Locale }) {
  const t = modenmisupporterStrings[locale];

  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Responsive/WebP background via <Img> instead of a CSS background-image */}
        <Img
          src="/wp-content/uploads/MISupportIT_NTK4170-scaled.jpg"
          alt=""
          aria-hidden
          sizes={SIZES.full}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/70 to-background/90" aria-hidden />
        <div className="relative mx-auto max-w-4xl px-4 md:px-8 py-28 md:py-40 text-center">
          <p className="text-sm uppercase tracking-widest text-primary font-semibold">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight">{t.hero.title}</h1>
          <p className="mt-6 text-lg md:text-xl text-foreground/90 leading-relaxed">{t.hero.body}</p>
          <p className="mt-4 text-base md:text-lg text-foreground/80">{t.hero.tagline}</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="mt-20 text-3xl md:text-4xl font-bold tracking-tight text-center">{t.team.heading}</h2>
          <p className="mt-6 mx-auto max-w-3xl text-center text-foreground/85 leading-relaxed">{t.team.body}</p>

          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {supporters.map((p) => {
              const linkLabel =
                p.id === "noreen-thomas"
                  ? t.cardLinkNoreenThomas
                  : t.cardLink.replace("{name}", p.name.split(" ")[0]);
              return (
                <Link
                  key={p.id}
                  to={supporterPath(p, locale)}
                  className="group rounded-2xl bg-secondary overflow-hidden flex flex-col hover:shadow-xl transition-shadow"
                >
                  <div className="overflow-hidden">
                    <div className="group-hover:scale-105 transition-transform duration-500">
                      <Img
                        src={p.quoteImg ?? p.img}
                        alt={p.name}
                        className={cn(
                          "w-full aspect-[4/3] object-cover",
                          p.imgObjectPosition ?? "object-[65%_top]",
                          p.imgScaleClass,
                        )}
                        sizes={SIZES.card3}
                      />
                    </div>
                  </div>

                  <div className="p-7 flex flex-col flex-1">
                    <div>
                      <p className="font-bold">{p.name}</p>
                      <p className="text-sm text-foreground/70">{tx(p.role, locale)}</p>
                    </div>
                    <p className="mt-4 italic text-foreground/90 leading-relaxed">"{tx(p.quote, locale)}"</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                      {linkLabel}
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
